// 📄 pages/Proyectos/DetalleProyecto.tsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Building2, Pencil, LogIn, Archive, ArchiveRestore, Server, Globe2, FileText } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useProject } from '../../context/ProjectContext.tsx';
import { getCliente } from '../../services/clienteService.ts';
import { setProjectArchived, getMemberPermisos } from '../../services/projectService.ts';
import { labelTipoErp } from './erpConfig.ts';
import { MODULOS, ModuloId, modulosDe, esProyectoSap } from './modulosConfig.ts';
import ConfirmDialog from '../../components/ui/ConfirmDialog.tsx';
import Miembros from './Miembros.tsx';

export default function DetalleProyecto() {
  const { user } = useAuth();
  const { project, projectId, esCliente } = useProject();
  const [clienteNombre, setClienteNombre] = useState<string | null>(null);
  const [clienteInfo, setClienteInfo] = useState<{ sector: string; pais: string; descripcion: string } | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  // Mismo criterio que ProjectSidebar.tsx: mientras carga (o si nunca se
  // configuró nada) se trata como "nada oculto" — el botón de acá abajo es
  // solo un atajo hacia la MISMA sección "editar" que ya gatea el sidebar,
  // así que tiene que respetar el mismo permiso, no solo `esCliente`.
  const [misSeccionesOcultas, setMisSeccionesOcultas] = useState<string[]>([]);

  useEffect(() => {
    if (!user || !projectId) { setMisSeccionesOcultas([]); return; }
    getMemberPermisos(projectId, user.uid)
      .then((permisos) => setMisSeccionesOcultas(permisos?.seccionesOcultas || []))
      .catch((err) => console.error('Error cargando mis permisos:', err));
  }, [projectId, user]);

  useEffect(() => {
    // Sub-fase G2: preferir el nombre desnormalizado — ver mismo comentario
    // en HomeCA.tsx (ProjectShell). Necesario para que un guest siga
    // viendo el nombre del cliente de SU proyecto aunque no sea miembro
    // de la org dueña de ese cliente.
    if (project?.clienteNombre) { setClienteNombre(project.clienteNombre); }
    else if (!project?.clienteId) { setClienteNombre(null); }
    if (!project?.clienteId) { setClienteInfo(null); return; }
    // Sector/país/descripción no se desnormalizan (a diferencia de
    // clienteNombre) — son informativos, no se usan en ningún otro lado
    // del sistema. Un guest sin acceso al doc `clientes/{id}` de la org
    // dueña simplemente no ve esta sección (falla en silencio, sin
    // console.error, a propósito: es esperado para ese caso, no un bug).
    getCliente(project.clienteId)
      .then((cliente) => {
        if (!project.clienteNombre) setClienteNombre(cliente?.name || null);
        setClienteInfo(cliente ? { sector: cliente.sector || '', pais: cliente.pais || '', descripcion: cliente.descripcion || '' } : null);
      })
      .catch(() => setClienteInfo(null));
  }, [project?.clienteId, project?.clienteNombre]);

  if (!project) return null;

  const esCreador = !!user && user.uid === project.creadoPor;

  const handleArchivar = async () => {
    setSaving(true);
    try {
      await setProjectArchived(projectId, true);
      setShowConfirm(false);
    } catch (err) {
      console.error('Error al archivar el proyecto:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h2 className="text-xl font-semibold text-white">{project.nombre}</h2>
              {project.archivado && (
                <span className="text-[10px] bg-gray-700 text-gray-300 border border-white/10 px-2 py-0.5 rounded-full uppercase tracking-wider">
                  Archivado
                </span>
              )}
            </div>
            <p className="text-sm text-gray-400 flex items-center gap-1.5">
              <Building2 className="w-3.5 h-3.5" /> {project.clienteId ? (clienteNombre || 'Cliente desconocido') : 'Proyecto interno (sin cliente)'}
            </p>
            {esProyectoSap(project) && (
              <p className="text-sm text-gray-400 flex items-center gap-1.5 mt-1">
                <Server className="w-3.5 h-3.5" />
                {project.erpTipo
                  ? `${labelTipoErp(project.erpTipo)}${project.erpVersion ? ` · v${project.erpVersion}` : ''}`
                  : 'ERP no especificado'}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {modulosDe(project).map((m) => MODULOS[m as ModuloId] && (
                <span key={m} className="text-[11px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-300">
                  {MODULOS[m as ModuloId].icon} {MODULOS[m as ModuloId].label}
                </span>
              ))}
            </div>
          </div>
          {!esCliente && !misSeccionesOcultas.includes('editar') && (
            <Link
              to={`/proyecto/${projectId}/editar`}
              className="flex items-center gap-1.5 text-xs bg-white/5 hover:bg-white/10 border border-white/10 px-3 py-1.5 rounded-lg transition-all whitespace-nowrap"
            >
              <Pencil className="w-3.5 h-3.5" /> Editar
            </Link>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Link
            to={`/proyecto/${projectId}`}
            className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
          >
            <LogIn className="w-4 h-4" /> Entrar al proyecto
          </Link>

          {esCreador && !esCliente && !project.archivado && (
            <button
              onClick={() => setShowConfirm(true)}
              className="inline-flex items-center gap-2 bg-white/5 hover:bg-red-500/10 border border-white/10 hover:border-red-500/30 text-gray-300 hover:text-red-400 px-4 py-2 rounded-lg text-sm font-semibold transition-all"
            >
              <Archive className="w-4 h-4" /> Archivar Proyecto
            </button>
          )}

          {project.archivado && (
            <span className="inline-flex items-center gap-2 text-xs text-gray-500">
              <ArchiveRestore className="w-3.5 h-3.5" />
              {esCreador ? 'Podés desarchivarlo desde "Mis Proyectos".' : 'Este proyecto está archivado.'}
            </span>
          )}
        </div>
      </div>

      {project.clienteId && clienteInfo && (clienteInfo.sector || clienteInfo.pais || clienteInfo.descripcion) && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 space-y-3">
          <h3 className="text-sm font-semibold text-white">Sobre el cliente</h3>
          <div className="flex flex-wrap gap-4">
            {clienteInfo.sector && (
              <p className="text-sm text-gray-400 flex items-center gap-1.5">
                <Building2 className="w-3.5 h-3.5" /> {clienteInfo.sector}
              </p>
            )}
            {clienteInfo.pais && (
              <p className="text-sm text-gray-400 flex items-center gap-1.5">
                <Globe2 className="w-3.5 h-3.5" /> {clienteInfo.pais}
              </p>
            )}
          </div>
          {clienteInfo.descripcion && (
            <p className="text-sm text-gray-300 flex items-start gap-1.5 whitespace-pre-wrap">
              <FileText className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-gray-500" /> {clienteInfo.descripcion}
            </p>
          )}
        </div>
      )}

      <Miembros />

      {showConfirm && (
        <ConfirmDialog
          title="¿Archivar este proyecto?"
          message="Podrás verlo de nuevo activando el filtro de archivados en Mis Proyectos. No se borra ningún dato ni progreso."
          confirmLabel={saving ? 'Archivando...' : 'Archivar'}
          danger
          onConfirm={handleArchivar}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}
